import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {mapParseList, zNumberToString} from '@brunata-angular/util';
import {Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {z} from 'zod';

export const customizedExportChannelSchema = z.object({
    modelConfigurationId: zNumberToString.optionalUntilDeployed('AGI-19864'),
    vendorName: z.string().nullable().optionalUntilDeployed('AGI-19929'),
    model: z.string(),
    obisCode: z.string().nullable(),
    prettyName: z.string().nullable(),
    unit: z.number().nullable(),
    visualizeCode: z.string().nullable(),
    primary: z.boolean(),
});

export type CustomizedExportChannel = z.infer<typeof customizedExportChannelSchema>;

export const customizedExportSuperTypeChannelsSchema = z.object({
    meterSuperTypeNo: zNumberToString,
    hasChannelLessMeters: z.boolean().optionalUntilDeployed('AGI-19864'),
    channels: z.array(customizedExportChannelSchema),
});

export type CustomizedExportSuperTypeChannels = z.infer<
    typeof customizedExportSuperTypeChannelsSchema
>;

export function customizedExportChannelKey(channel: CustomizedExportChannel): string {
    return JSON.stringify([channel.model, channel.obisCode, channel.unit, channel.visualizeCode]);
}

// The picker tells two vendors' same-named models apart by the vendor name, which only reaches this
// endpoint with AGI-19929's backend half - so it is the one part of a model's identity that moves
// under anything stored across that deploy. The id nests the key the draft persists without it.
export function meterModelKey(meterSuperTypeNo: string, channel: CustomizedExportChannel): string {
    return JSON.stringify([meterSuperTypeNo, channel.model]);
}

export function meterModelId(meterSuperTypeNo: string, channel: CustomizedExportChannel): string {
    return JSON.stringify([meterModelKey(meterSuperTypeNo, channel), channel.vendorName]);
}

const meterModelIdParts = z.tuple([z.string(), z.string().nullable()]);

export function meterModelKeyOf(modelId: string): string {
    return meterModelIdParts.parse(JSON.parse(modelId) as unknown)[0];
}

export function meterChannelId(meterSuperTypeNo: string, channel: CustomizedExportChannel): string {
    return JSON.stringify([
        meterSuperTypeNo,
        channel.visualizeCode,
        channel.prettyName,
        channel.unit,
    ]);
}

@Injectable({providedIn: 'root'})
export class ChannelsService {
    private readonly baseUrl = environment.onlineServiceUrl;
    private readonly http = inject(HttpClient);

    // One group per requested super type, in request order; a group's channels may be empty.
    getAvailableChannels(
        meterSuperTypeNos: string[],
    ): Observable<CustomizedExportSuperTypeChannels[]> {
        return this.http
            .get(`${this.baseUrl}/customizedexports/channels`, {
                params: {superTypes: meterSuperTypeNos.join(',')},
                withCredentials: true,
            })
            .pipe(mapParseList(customizedExportSuperTypeChannelsSchema));
    }
}
